import { useMemo, useState } from 'react';
import { FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp } from 'react-icons/fi';

const DEFAULT_PAGE_SIZE = 10;

// Figma draws a vertical rule after some columns (e.g. Unit on Market Prices,
// Contact on Veterinarians) — opt in per column with `divider: true`.
const dividerStyle = (col) => (col.divider ? { borderRight: '0.65px solid #e8e8e8' } : undefined);

/**
 * PublicDataTable — custom table styled exactly to the public-module Figma design.
 *
 * Props
 *   columns     Array<{ key, title, render? (value, row) => node, divider? boolean }>
 *   data        Array<object>
 *   searchQuery string  — external live-search value passed from the parent page
 *   pageSize    number  (default 10)
 */
export default function PublicDataTable({
  columns  = [],
  data     = [],
  searchQuery = '',
  pageSize = DEFAULT_PAGE_SIZE,
}) {
  const [page,    setPage]    = useState(1);
  // sortKey: column key currently sorted on  |  null = unsorted
  // sortDir: 'asc' | 'desc'
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // ── 1. Filter ──────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) =>
      columns.some((col) => {
        const raw = row[col.key];
        return raw != null && String(raw).toLowerCase().includes(q);
      })
    );
  }, [data, columns, searchQuery]);

  // ── 2. Sort ────────────────────────────────────────────────────
  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      // Numeric comparison when both values are numbers
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return sortDir === 'asc' ? -1 :  1;
      if (as > bs) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  // ── 3. Paginate ────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const start      = (safePage - 1) * pageSize;
  const pageRows   = sorted.slice(start, start + pageSize);

  const goTo = (p) => setPage(Math.max(1, Math.min(totalPages, p)));

  // Click on a header toggles sort direction; clicking a new header starts asc
  function handleSort(key) {
    if (sortKey === key) {
      // Already sorted on this column — flip direction
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  // Page button list — at most 5 around current page
  const pageButtons = useMemo(() => {
    const buttons = [];
    const delta = 2;
    const lo = Math.max(1, safePage - delta);
    const hi = Math.min(totalPages, safePage + delta);
    for (let i = lo; i <= hi; i++) buttons.push(i);
    return buttons;
  }, [safePage, totalPages]);

  return (
    <div className="flex flex-col gap-4">

      {/* Table — horizontal scroll on mobile */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {columns.map((col, ci) => {
                const isActive = sortKey === col.key;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    onClick={() => handleSort(col.key)}
                    style={{ borderTop: '0.65px solid #e8e8e8', borderBottom: '0.45px solid #e8e8e8', ...dividerStyle(col) }}
                    className={[
                      'group cursor-pointer select-none',
                      'bg-[#f9fafc] px-[15px] py-[15px]',
                      'text-[11px] font-semibold text-black',
                      'whitespace-nowrap text-left',
                      'transition-colors hover:bg-[#f1f4f9]',
                      isActive ? 'text-[#135809]' : '',
                      ci === 0 ? 'rounded-tl-[8px]' : '',
                      ci === columns.length - 1 ? 'rounded-tr-[8px]' : '',
                    ].join(' ')}
                  >
                    <span className="flex items-center gap-1.5">
                      {col.title}
                      {/* Icon: always mounted but visible only on hover or when active */}
                      <span className={[
                        'flex h-3.5 w-2.5 shrink-0 items-center justify-center transition-opacity',
                        isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-50',
                      ].join(' ')}>
                        {isActive && sortDir === 'desc'
                          ? <FiChevronDown className="h-3 w-3" />
                          : <FiChevronUp   className="h-3 w-3" />}
                      </span>
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="bg-white px-[15px] py-6 text-center text-[11px] font-medium text-[#8e8e8e]"
                  style={{ borderBottom: '0.65px solid #e8e8e8' }}
                >
                  No records found.
                </td>
              </tr>
            ) : (
              pageRows.map((row, ri) => {
                const isLast = ri === pageRows.length - 1;
                return (
                  <tr key={row.id ?? ri} className="transition-colors hover:bg-[#fafafa]">
                    {columns.map((col, ci) => {
                      const value     = row[col.key];
                      const displayed = col.render ? col.render(value, row) : (value ?? '—');
                      return (
                        <td
                          key={col.key}
                          style={{ borderBottom: '1px solid #e8e8e8', ...dividerStyle(col) }}
                          className={[
                            'bg-white p-[15px]',
                            'text-[11px] font-medium text-black',
                            'whitespace-nowrap',
                            isLast && ci === 0 ? 'rounded-bl-[8px]' : '',
                            isLast && ci === columns.length - 1 ? 'rounded-br-[8px]' : '',
                          ].join(' ')}
                        >
                          {displayed}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-4 px-1">
          <p className="text-[11px] font-medium text-[#8e8e8e]">
            Showing {start + 1}–{Math.min(start + pageSize, sorted.length)} of {sorted.length} records
          </p>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => goTo(safePage - 1)}
              disabled={safePage === 1}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-[#e8e8e8] bg-white text-[#606060] transition hover:bg-[#f9fafc] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FiChevronLeft className="h-3.5 w-3.5" />
            </button>

            {pageButtons[0] > 1 && (
              <>
                <PageBtn n={1} current={safePage} onGo={goTo} />
                {pageButtons[0] > 2 && <span className="px-1 text-[11px] text-[#8e8e8e]">…</span>}
              </>
            )}

            {pageButtons.map((n) => (
              <PageBtn key={n} n={n} current={safePage} onGo={goTo} />
            ))}

            {pageButtons[pageButtons.length - 1] < totalPages && (
              <>
                {pageButtons[pageButtons.length - 1] < totalPages - 1 && (
                  <span className="px-1 text-[11px] text-[#8e8e8e]">…</span>
                )}
                <PageBtn n={totalPages} current={safePage} onGo={goTo} />
              </>
            )}

            <button
              type="button"
              onClick={() => goTo(safePage + 1)}
              disabled={safePage === totalPages}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-[#e8e8e8] bg-white text-[#606060] transition hover:bg-[#f9fafc] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <FiChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

function PageBtn({ n, current, onGo }) {
  const isActive = n === current;
  return (
    <button
      type="button"
      onClick={() => onGo(n)}
      className={[
        'flex h-7 w-7 items-center justify-center rounded-md border text-[8px] font-semibold transition',
        isActive
          ? 'border-[#21990f] bg-[rgba(26,121,12,0.08)] text-[#135809]'
          : 'border-[#e8e8e8] bg-white text-[#606060] hover:bg-[#f9fafc]',
      ].join(' ')}
    >
      {n}
    </button>
  );
}
